import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Copy, Check, CreditCard } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto } from '../utils/format';
import { filaClickeable, FOCO_FILA } from '../utils/filaClickeable';
import Modal from '../components/Modal';
import BotonAyuda from '../components/BotonAyuda';

const FORM_VACIO = { nombre: '', tipo: 'banco', banco: '', numero_cuenta: '', moneda: 'USD', saldo_inicial: '', saldo_minimo: '', activo: true, es_fiscal: true };

export default function Cuentas() {
  const navigate = useNavigate();
  const { empresaActiva, empresas } = useAuth();
  const empresaNombre = empresas.find((e) => String(e.empresa_id) === String(empresaActiva))?.empresa_nombre;
  const [cuentas, setCuentas] = useState([]);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [error, setError] = useState('');

  const [tarjetas, setTarjetas] = useState([]);
  const [mostrarFormTarjeta, setMostrarFormTarjeta] = useState(false);
  const [editandoTarjetaId, setEditandoTarjetaId] = useState(null);
  const [formTarjeta, setFormTarjeta] = useState({ titulo: '', contenido: '' });
  const [errorTarjeta, setErrorTarjeta] = useState('');
  const [copiadoId, setCopiadoId] = useState(null);

  async function cargar() {
    const { data } = await api.get('/cuentas');
    setCuentas(data);
  }

  async function cargarTarjetas() {
    const { data } = await api.get('/tarjetas-datos');
    setTarjetas(data);
  }

  useEffect(() => {
    if (empresaActiva) {
      cargar();
      cargarTarjetas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva]);

  function empezarEdicion(cuenta) {
    setEditandoId(cuenta.id);
    setForm({
      nombre: cuenta.nombre,
      tipo: cuenta.tipo,
      banco: cuenta.banco || '',
      numero_cuenta: cuenta.numero_cuenta || '',
      moneda: cuenta.moneda,
      saldo_inicial: cuenta.saldo_inicial,
      saldo_minimo: cuenta.saldo_minimo ?? '',
      activo: cuenta.activo === undefined ? true : !!Number(cuenta.activo),
      es_fiscal: cuenta.es_fiscal === undefined ? true : !!Number(cuenta.es_fiscal)
    });
    setMostrarForm(true);
  }

  function cancelar() {
    setMostrarForm(false);
    setEditandoId(null);
    setForm(FORM_VACIO);
  }

  async function guardar(e) {
    e.preventDefault();
    setError('');
    try {
      if (editandoId) {
        await api.put(`/cuentas/${editandoId}`, form);
      } else {
        await api.post('/cuentas', form);
      }
      cancelar();
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar la cuenta');
    }
  }

  async function eliminar(id) {
    if (!window.confirm('¿Eliminar esta cuenta? Solo se puede si no tiene movimientos registrados.')) return;
    try {
      await api.delete(`/cuentas/${id}`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar la cuenta');
    }
  }

  function empezarEdicionTarjeta(t) {
    setEditandoTarjetaId(t.id);
    setFormTarjeta({ titulo: t.titulo, contenido: t.contenido });
    setMostrarFormTarjeta(true);
  }

  function cancelarTarjeta() {
    setMostrarFormTarjeta(false);
    setEditandoTarjetaId(null);
    setFormTarjeta({ titulo: '', contenido: '' });
    setErrorTarjeta('');
  }

  async function guardarTarjeta(e) {
    e.preventDefault();
    setErrorTarjeta('');
    try {
      if (editandoTarjetaId) {
        await api.put(`/tarjetas-datos/${editandoTarjetaId}`, formTarjeta);
      } else {
        await api.post('/tarjetas-datos', formTarjeta);
      }
      cancelarTarjeta();
      cargarTarjetas();
    } catch (err) {
      setErrorTarjeta(err.response?.data?.error || 'Error al guardar la tarjeta');
    }
  }

  async function eliminarTarjeta(id) {
    if (!window.confirm('¿Eliminar esta tarjeta?')) return;
    try {
      await api.delete(`/tarjetas-datos/${id}`);
      cargarTarjetas();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar la tarjeta');
    }
  }

  // Agrupadas por moneda, orden alfabético de moneda y de nombre dentro de cada una.
  const cuentasAgrupadas = (() => {
    const grupos = {};
    cuentas.forEach((c) => {
      if (!grupos[c.moneda]) grupos[c.moneda] = [];
      grupos[c.moneda].push(c);
    });
    return Object.keys(grupos)
      .sort((a, b) => a.localeCompare(b, 'es'))
      .map((moneda) => ({
        moneda,
        cuentas: [...grupos[moneda]].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
      }));
  })();

  async function copiar(t) {
    try {
      await navigator.clipboard.writeText(t.contenido);
      setCopiadoId(t.id);
      setTimeout(() => setCopiadoId((actual) => (actual === t.id ? null : actual)), 1500);
    } catch (err) {
      alert('No se pudo copiar al portapapeles');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-slate-800">
          Cuentas
          {empresaNombre && <span className="text-slate-400 font-normal"> · {empresaNombre}</span>}
          <BotonAyuda />
        </h1>
        <button
          onClick={() => setMostrarForm(true)}
          title="Nueva cuenta"
          className="bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-md self-start sm:self-auto"
        >
          <Plus size={18} />
        </button>
      </div>

      {mostrarForm && (
        <Modal title={editandoId ? 'Editando cuenta' : 'Nueva cuenta'} onClose={cancelar}>
        <form onSubmit={guardar} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input
            required
            placeholder="Nombre de la cuenta (ej. BAC Corriente)"
            value={form.nombre}
            onChange={(e) => setForm({ ...form, nombre: e.target.value })}
            className="border rounded-md px-3 py-2 sm:col-span-2"
          />

          <select
            value={form.tipo}
            onChange={(e) => setForm({ ...form, tipo: e.target.value })}
            className="border rounded-md px-3 py-2"
          >
            <option value="banco">Banco</option>
            <option value="caja_chica">Caja chica</option>
            <option value="tarjeta">Tarjeta</option>
            <option value="otro">Otro</option>
          </select>

          <select
            value={form.moneda}
            onChange={(e) => setForm({ ...form, moneda: e.target.value })}
            className="border rounded-md px-3 py-2"
          >
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="MXN">MXN</option>
            <option value="BS">BS</option>
          </select>

          <input
            placeholder="Banco (opcional)"
            value={form.banco}
            onChange={(e) => setForm({ ...form, banco: e.target.value })}
            className="border rounded-md px-3 py-2"
          />

          <input
            placeholder="Número de cuenta (opcional)"
            value={form.numero_cuenta}
            onChange={(e) => setForm({ ...form, numero_cuenta: e.target.value })}
            className="border rounded-md px-3 py-2"
          />

          <div className="sm:col-span-2">
            <input
              type="number"
              step="0.01"
              placeholder="Saldo inicial"
              value={form.saldo_inicial}
              onChange={(e) => setForm({ ...form, saldo_inicial: e.target.value })}
              className="border rounded-md px-3 py-2 w-full"
            />
            {editandoId && (
              <p className="text-xs text-slate-400 mt-1">
                Si lo cambias, el saldo actual se ajusta automáticamente por la diferencia (sin afectar los movimientos ya registrados).
              </p>
            )}
          </div>

          <div className="sm:col-span-2">
            <input
              type="number"
              step="0.01"
              placeholder="Saldo mínimo (opcional)"
              value={form.saldo_minimo}
              onChange={(e) => setForm({ ...form, saldo_minimo: e.target.value })}
              className="border rounded-md px-3 py-2 w-full"
            />
            <p className="text-xs text-slate-400 mt-1">
              Saldo mínimo, en la moneda de la cuenta: si el saldo baja de aquí sale una alerta, y el flujo proyectado avisa si se va a caer por debajo.
            </p>
          </div>

          {editandoId && (
            <label className="sm:col-span-2 flex items-start gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={form.activo}
                onChange={(e) => setForm({ ...form, activo: e.target.checked })}
                className="mt-0.5"
              />
              <span>
                Cuenta activa
                <span className="block text-xs text-slate-400">Desmarcada = inactiva: no admite movimientos, pagos, cobranzas ni transferencias nuevas, y no suma en la disponibilidad ni en el flujo proyectado. Su historial se conserva y sigue en los reportes.</span>
              </span>
            </label>
          )}

          <label className="sm:col-span-2 flex items-start gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={form.es_fiscal}
              onChange={(e) => setForm({ ...form, es_fiscal: e.target.checked })}
              className="mt-0.5"
            />
            <span>
              Cuenta fiscal
              <span className="block text-xs text-slate-400">Desmarcada = no fiscal (paralelo): lo que entra aquí no declara IVA.</span>
            </span>
          </label>

          {error && <p className="text-red-600 text-sm sm:col-span-2">{error}</p>}

          <button type="submit" className="bg-primary-600 text-white rounded-md py-2 sm:col-span-2 hover:bg-primary-700">
            {editandoId ? 'Guardar cambios' : 'Guardar'}
          </button>
        </form>
        </Modal>
      )}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="p-3">Nombre</th>
                <th className="p-3">Tipo</th>
                <th className="p-3">Banco</th>
                <th className="p-3">Moneda</th>
                <th className="p-3 text-right">Saldo actual</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {cuentasAgrupadas.map((grupo) => (
                <React.Fragment key={grupo.moneda}>
                  <tr className="border-t bg-slate-50 hover:bg-slate-100 transition-colors">
                    <td colSpan={6} className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                      {grupo.moneda}
                    </td>
                  </tr>
                  {grupo.cuentas.map((c) => (
                    <tr
                      key={c.id}
                      {...filaClickeable(() => navigate(`/movimientos?cuenta_id=${c.id}`))}
                      className={`border-t hover:bg-slate-50 transition-colors cursor-pointer ${FOCO_FILA}`}
                    >
                      <td className="p-3 font-medium whitespace-nowrap">
                        {c.nombre}
                        {c.es_fiscal !== undefined && !Number(c.es_fiscal) && (
                          <span className="ml-2 text-xs font-normal px-2 py-0.5 rounded-full bg-slate-800 text-white">No fiscal</span>
                        )}
                        {c.activo !== undefined && !Number(c.activo) && (
                          <span className="ml-2 text-xs font-normal px-2 py-0.5 rounded-full bg-slate-200 text-slate-500">Inactiva</span>
                        )}
                      </td>
                      <td className="p-3 capitalize whitespace-nowrap">{c.tipo.replace('_', ' ')}</td>
                      <td className="p-3 whitespace-nowrap">{c.banco || '-'}</td>
                      <td className="p-3">{c.moneda}</td>
                      <td className="p-3 text-right font-medium whitespace-nowrap">
                        {c.moneda} {c.moneda !== 'BS' && '$'}{formatMonto(c.saldo_actual)}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => empezarEdicion(c)}
                            title="Editar"
                            className="text-slate-400 hover:text-primary-600 transition-colors"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => eliminar(c.id)}
                            title="Eliminar"
                            className="text-slate-400 hover:text-red-600 transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              {cuentas.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-slate-400">
                    No hay cuentas creadas todavía.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold text-slate-800">Datos para compartir</h2>
          <button
            onClick={() => setMostrarFormTarjeta(true)}
            title="Nueva tarjeta"
            className="bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-md self-start sm:self-auto"
          >
            <Plus size={18} />
          </button>
        </div>
        <p className="text-sm text-slate-500">
          Textos listos para copiar y enviar a clientes o proveedores (RIF, cédula, números de cuenta, etc.).
        </p>

        {mostrarFormTarjeta && (
          <Modal title={editandoTarjetaId ? 'Editando tarjeta' : 'Nueva tarjeta'} onClose={cancelarTarjeta}>
          <form onSubmit={guardarTarjeta} className="grid grid-cols-1 gap-4">
            <input
              required
              placeholder="Título (ej. RIF de la empresa, Cuenta USD Banesco)"
              value={formTarjeta.titulo}
              onChange={(e) => setFormTarjeta({ ...formTarjeta, titulo: e.target.value })}
              className="border rounded-md px-3 py-2"
            />
            <textarea
              required
              rows={3}
              placeholder="Contenido a copiar"
              value={formTarjeta.contenido}
              onChange={(e) => setFormTarjeta({ ...formTarjeta, contenido: e.target.value })}
              className="border rounded-md px-3 py-2 font-mono text-sm"
            />
            {errorTarjeta && <p className="text-red-600 text-sm">{errorTarjeta}</p>}
            <button type="submit" className="bg-primary-600 text-white rounded-md py-2 hover:bg-primary-700">
              {editandoTarjetaId ? 'Guardar cambios' : 'Guardar'}
            </button>
          </form>
          </Modal>
        )}

        {tarjetas.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-6 text-center text-slate-400 text-sm">
            No hay tarjetas creadas todavía.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {tarjetas.map((t) => (
              <div
                key={t.id}
                onClick={() => copiar(t)}
                title="Click para copiar"
                className={`relative bg-white rounded-xl shadow-sm p-3 flex flex-col gap-1.5 cursor-pointer border transition-colors ${
                  copiadoId === t.id ? 'border-green-400 bg-green-50' : 'border-transparent hover:border-primary-300'
                }`}
              >
                <div className="flex items-start justify-between gap-1">
                  <div className="flex items-center gap-1.5 text-slate-700 font-medium text-xs min-w-0">
                    <CreditCard size={13} className="text-primary-600 shrink-0" />
                    <span className="break-words truncate">{t.titulo}</span>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => empezarEdicionTarjeta(t)}
                      title="Editar"
                      className="text-slate-400 hover:text-primary-600 transition-colors"
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      onClick={() => eliminarTarjeta(t.id)}
                      title="Eliminar"
                      className="text-slate-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
                <p className="text-xs text-slate-600 font-mono whitespace-pre-wrap break-words flex-1">
                  {t.contenido}
                </p>
                <span className="flex items-center gap-1 text-[11px] text-slate-400">
                  {copiadoId === t.id ? (
                    <span className="flex items-center gap-1 text-green-600 font-medium">
                      <Check size={11} /> Copiado
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <Copy size={11} /> Copiar
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}